import { eui } from "ems-ui"
import { getToken } from "@/utils/auth"
// import WSConfig from "./ws-config"


class WSRequest {
  constructor(url) {
    this.ws = null
    this.userId = null
    this.timeout = 10 * 1000

    // 待处理的请求 Map: key -> { resolve, reject, timeoutId }
    this.pendingRequests = new Map()

    // 消息队列（连接建立前的请求）
    this.messageQueue = []

    // 事件监听器引用（用于清理）
    this.messageHandler = null
    this.openHandler = null
    this.errorHandler = null
    this.closeHandler = null

    // 重连相关
    this.connectCount = 0
    this.connectTimer = null
    this.maxReconnectAttempts = 5
    this.reconnectDelay = 1000 // 初始重连延迟

    // 心跳相关
    this.heartbeatInterval = null
    this.heartbeatTimeout = 30000 // 30秒心跳

    // url设置
    if (Array.isArray(url) && url.length > 0) {
      this.url = url[0]
      this.altUrls = url
    } else {
      this.url = url
      this.altUrls = [url]
    }
    this.altIndex = 0
  }

  get is_open() {
    if (this.ws) {
      return this.ws.readyState === WebSocket.OPEN
    }
    return false
  }

  get is_connecting() {
    if (this.ws) {
      return this.ws.readyState === WebSocket.CONNECTING
    }
    return false
  }

  request(method, params) {
    return new Promise((resolve, reject) => {
      console.log('send -> ' + method)
      if (!method) {
        reject(new Error('需要业务类型方法'))
        return
      }

      const data = {
        method: method,
        params: params || {},
        token: 'Bearer ' + getToken(),
        timestamp: Date.now(),
      }

      const reqKey = this.getKey(data)

      // 设置超时
      const timeoutId = setTimeout(() => {
        if (this.pendingRequests.has(reqKey)) {
          this.pendingRequests.delete(reqKey)
          reject(new Error('获取数据超时！'))
        }
      }, this.timeout)

      // 保存 Promise 的 resolve/reject
      this.pendingRequests.set(reqKey, { resolve, reject, timeoutId })

      // 如果连接已建立，直接发送；否则加入队列
      if (this.is_open) {
        this.send(data)
      } else if (this.is_connecting) {
        this.messageQueue.push(data)
      } else {
        this.pendingRequests.delete(reqKey)
        clearTimeout(timeoutId)
        reject(new Error('WebSocket 未连接'))
      }
    })
  }

  getKey(RObj) {
    return RObj.method + '-' + RObj.timestamp
  }

  send(obj) {
    if (this.is_open) {
      const str = JSON.stringify(obj)
      this.ws.send(str)
    } else {
      console.error('请在WebSocket连接后再发送数据')
    }
  }

  connect(userId = '') {
    this.userId = userId
    console.log('[ws] 正在连接: ' + this.url + userId)

    // 清理旧连接
    this.cleanup()

    this.ws = new WebSocket(this.url + userId)

    this.addOpenListener()
    this.addMessageListener()
    this.addErrorListener()
    this.addCloseListener()
  }

  reconnect() {
    // 清理旧连接和定时器
    this.cleanup()

    // 切换备用 URL
    this.url = this.altUrls[this.altIndex++ % this.altUrls.length]

    console.log('[ws] 切换到备用地址: ' + this.url)
    this.ws = new WebSocket(this.url + this.userId)

    this.addOpenListener()
    this.addMessageListener()
    this.addErrorListener()
    this.addCloseListener()
  }

  close() {
    this.cleanup()
    // 停止重连
    if (this.connectTimer) {
      clearTimeout(this.connectTimer)
      this.connectTimer = null
    }
  }

  cleanup() {
    // 停止心跳
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }

    // 移除所有事件监听器
    if (this.ws) {
      if (this.openHandler) {
        this.ws.removeEventListener('open', this.openHandler)
      }
      if (this.messageHandler) {
        this.ws.removeEventListener('message', this.messageHandler)
      }
      if (this.errorHandler) {
        this.ws.removeEventListener('error', this.errorHandler)
      }
      if (this.closeHandler) {
        this.ws.removeEventListener('close', this.closeHandler)
      }

      // 关闭 WebSocket
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close()
      }
    }

    this.openHandler = null
    this.messageHandler = null
    this.errorHandler = null
    this.closeHandler = null
  }

  addOpenListener() {
    this.openHandler = () => {
      console.log('[ws] 连接成功')

      // 重置重连计数和延迟
      this.connectCount = 0
      this.reconnectDelay = 1000

      // 发送队列中的消息
      while (this.messageQueue.length > 0) {
        const data = this.messageQueue.shift()
        this.send(data)
      }

      // 启动心跳
      this.startHeartbeat()
    }
    this.ws.addEventListener('open', this.openHandler)
  }

  addMessageListener() {
    this.messageHandler = (e) => {
      try {
        const result = JSON.parse(e.data)

        // 心跳响应
        if (result.method === 'heartbeat') {
          return
        }

        const key = this.getKey(result)

        if (this.pendingRequests.has(key)) {
          const { resolve, reject, timeoutId } = this.pendingRequests.get(key)
          clearTimeout(timeoutId)
          this.pendingRequests.delete(key)

          console.log('receive <-- ' + result.method)

          if (result.code == 200) {
            resolve(result.data)
          } else {
            const error = new Error(result.msg || result.message || '请求失败')
            reject(error)
            eui.showToast(result.msg || result.message, 'error')
          }
        } else if (result.code != 200) {
          // 无对应请求的错误消息
          eui.showToast(result.msg || result.message, 'error')
        }
      } catch (error) {
        console.error('[ws] 消息解析失败:', error)
      }
    }
    this.ws.addEventListener('message', this.messageHandler)
  }

  addErrorListener() {
    this.errorHandler = (event) => {
      console.error('[ws] 连接出错:', event)
    }
    this.ws.addEventListener('error', this.errorHandler)
  }

  addCloseListener() {
    this.closeHandler = (event) => {
      console.warn('[ws] 连接已断开, code:', event.code, 'reason:', event.reason)

      // 停止心跳
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval)
        this.heartbeatInterval = null
      }

      // 拒绝所有待处理的请求
      this.pendingRequests.forEach(({ reject, timeoutId }) => {
        clearTimeout(timeoutId)
        reject(new Error('WebSocket 连接已断开'))
      })
      this.pendingRequests.clear()

      // 尝试重连
      this.tryReconnect()
    }
    this.ws.addEventListener('close', this.closeHandler)
  }

  tryReconnect() {
    if (this.connectCount >= this.maxReconnectAttempts) {
      console.error('[ws] 已达到最大重连次数，停止重连')
      eui.showToast('WebSocket 连接失败，请刷新页面重试', 'error')
      return
    }

    // 指数退避算法
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.connectCount), 30000)

    console.warn(`[ws] 将在 ${delay}ms 后进行第 ${this.connectCount + 1} 次重连...`)

    this.connectTimer = setTimeout(() => {
      this.connectCount++
      this.reconnect()
    }, delay)
  }

  startHeartbeat() {
    // 清除旧的心跳
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
    }

    // // 每30秒发送一次心跳
    // this.heartbeatInterval = setInterval(() => {
    //   if (this.is_open) {
    //     this.send({
    //       method: 'heartbeat',
    //       params: {},
    //       timestamp: Date.now()
    //     })
    //   }
    // }, this.heartbeatTimeout)
  }

  /**
   * 增加备用url
   * @param {*} url 
   */
  addAltUrl(url) {
    this.altUrls.push(url)
  }

  /**
   * 设置超时时间
   * @param {number} timeout 毫秒
   */
  setTimeout(timeout) {
    this.timeout = timeout
  }

  /**
   * 设置最大重连次数
   * @param {number} max 
   */
  setMaxReconnectAttempts(max) {
    this.maxReconnectAttempts = max
  }
}


const emind_config = window.__emind_config || {}
const ws_req = new WSRequest(emind_config.ws_url)

export { ws_req }
